import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BudgetManager = ({ budgets }) => {
  const [editingBudget, setEditingBudget] = useState(null);

  const getAlertColor = (alert) => {
    switch (alert) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      default: return 'text-success';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 95) return 'bg-error';
    if (percentage >= 80) return 'bg-warning';
    return 'bg-success';
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Budget Management</h2>
          <p className="card-description">Track spending limits by category</p>
        </div>
        <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth flex items-center gap-2">
          <Icon name="Plus" size={16} />
          Add Budget
        </button>
      </div>

      <div className="card-content">
        <div className="space-y-4">
          {budgets?.map((budget, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${budget?.color}20` }}
                  >
                    <Icon name={budget?.icon} size={20} style={{ color: budget?.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{budget?.category}</p>
                    <p className="text-xs text-muted-foreground">
                      ${budget?.spent?.toFixed(2)} of ${budget?.limit?.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`text-lg font-bold ${getAlertColor(budget?.alert)}`}>
                      {budget?.percentage}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${(budget?.limit - budget?.spent)?.toFixed(2)} left
                    </p>
                  </div>
                  <button 
                    onClick={() => setEditingBudget(editingBudget === index ? null : index)}
                    className="p-2 hover:bg-muted rounded-lg transition-smooth"
                  >
                    <Icon name="Settings" size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="relative">
                <div className="w-full bg-muted rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full transition-all ${getProgressColor(budget?.percentage)}`}
                    style={{ width: `${Math.min(budget?.percentage, 100)}%` }}
                  />
                </div>
                {budget?.alert === 'critical' && (
                  <div className="absolute right-0 top-0 -translate-y-1/2">
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-error/10 rounded-full">
                      <Icon name="AlertCircle" size={12} className="text-error" />
                      <span className="text-xs font-semibold text-error">Alert</span>
                    </div>
                  </div>
                )}
              </div>

              {editingBudget === index && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Monthly Limit
                    </label>
                    <input
                      type="number"
                      defaultValue={budget?.limit}
                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Alert Threshold
                    </label>
                    <select className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                      <option value="80">80% of limit</option>
                      <option value="90">90% of limit</option>
                      <option value="95">95% of limit</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth">
                      Save Changes
                    </button>
                    <button 
                      onClick={() => setEditingBudget(null)}
                      className="px-3 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-smooth"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Budget Tips</p>
              <p className="text-xs text-muted-foreground">
                Set realistic limits based on your spending patterns. You'll receive alerts when approaching thresholds.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetManager;